import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  getWhatsAppSlots,
  createWhatsAppSlot,
  whatsAppNumberLink,
  assignWhatsAppNumber,
  unassignWhatsAppNumber,
  getWhatsAppQRCode,
  getAvailableSlot,
  linkCloudApiNumber,
  getCloudApiConnectionStatus,
  unlinkCloudApiAccount,
} from './services';
import { updateSlotPhone, calculateAvailabilityFlags } from './helpers';

export const WhatsAppSlice = createSlice({
  name: 'WhatsApp',
  initialState,
  reducers: {
    setWhatsAppData(state, action) {
      const { key, subkey, value } = action.payload;

      if (subkey) {
        state.dashboard[key][subkey] = value;
      } else {
        state.dashboard[key] = value;
      }
    },

    toggleAddNumberModal(state, action) {
      state.dashboard.addNumberModal.isOpen =
        !state.dashboard.addNumberModal.isOpen;

      if (!state.dashboard.addNumberModal.isOpen) {
        // Al cerrar, limpiar todo
        state.dashboard.addNumberModal.step = 0;
        state.dashboard.addNumberModal.selectedSlot = null;
        state.dashboard.addNumberModal.lastAssignedSlot = null;
        state.dashboard.addNumberModal.form = {
          phoneType: 'business',
          data: null,
        };
      } else if (action.payload) {
        // Al abrir, si viene un slot específico desde la tabla, guardarlo
        state.dashboard.addNumberModal.selectedSlot = action.payload;
      }
    },

    toggleLinkWhatsAppModal(state, action) {
      if (action.payload) {
        state.dashboard.linkWhatsAppModal.isOpen = true;
        state.dashboard.linkWhatsAppModal.selectedNumber = action.payload;
        // Resetear el flujo al abrir
        state.dashboard.linkWhatsAppModal.currentStep = 'info';
        state.dashboard.linkWhatsAppModal.isConfirmed = false;
      } else {
        state.dashboard.linkWhatsAppModal.isOpen = false;
        state.dashboard.linkWhatsAppModal.selectedNumber = null;
        state.dashboard.linkWhatsAppModal.linkedPhoneNumber = null;
        // Resetear el flujo al cerrar
        state.dashboard.linkWhatsAppModal.currentStep = 'info';
        state.dashboard.linkWhatsAppModal.isConfirmed = false;
      }
    },

    toggleLinkCloudApiAccountModal(state) {
      state.dashboard.linkCloudApiAccountModal.isOpen =
        !state.dashboard.linkCloudApiAccountModal.isOpen;
    },

    toggleUnlinkCloudApiAccountModal(state) {
      state.dashboard.unlinkCloudApiAccountModal.isOpen =
        !state.dashboard.unlinkCloudApiAccountModal.isOpen;
    },

    reportBillingEligibilityIssue(state, action) {
      state.dashboard.cloudApi.billingEligibilityIssue = {
        isActive: true,
        details: action.payload?.details ?? null,
        dismissed: false,
      };
    },

    hydrateBillingEligibilityIssueBanner(state, action) {
      state.dashboard.cloudApi.billingEligibilityIssue =
        action.payload ??
        initialState.dashboard.cloudApi.billingEligibilityIssue;
    },

    dismissBillingEligibilityIssueBanner(state) {
      state.dashboard.cloudApi.billingEligibilityIssue.dismissed = true;
    },

    setLinkWhatsAppModalStep(state, action) {
      state.dashboard.linkWhatsAppModal.currentStep = action.payload;
    },

    setLinkWhatsAppModalConfirmed(state, action) {
      state.dashboard.linkWhatsAppModal.isConfirmed = action.payload;
    },

    setWhatsAppNumberLinked(state, action) {
      const { numberId, linkedBy, formattedNumber } = action.payload;
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === numberId
      );
      if (numberIndex !== -1) {
        const slot = state.dashboard.numbers[numberIndex];

        // Usar keys de API (snake_case)
        slot.is_linked = true;
        if (linkedBy) {
          slot.linked_by = { name: linkedBy };
        }

        // Actualizar el número según el scope del slot usando helper
        updateSlotPhone(slot, formattedNumber);

        // Actualizar updated_at
        slot.updated_at = new Date().toISOString();
      }

      // Actualizar también el selectedNumber en el modal si coincide
      if (
        state.dashboard.linkWhatsAppModal.selectedNumber?.id === numberId &&
        formattedNumber
      ) {
        const selectedSlot = state.dashboard.linkWhatsAppModal.selectedNumber;
        updateSlotPhone(selectedSlot, formattedNumber);
      }
    },

    setWhatsAppNumberUnlinked(state, action) {
      const { numberId } = action.payload;
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === numberId
      );
      if (numberIndex !== -1) {
        const slot = state.dashboard.numbers[numberIndex];
        slot.is_linked = false;
        slot.linked_by = null;
        slot.updated_at = new Date().toISOString();
      }

      // Actualizar también el selectedNumber en el modal si coincide
      if (state.dashboard.linkWhatsAppModal.selectedNumber?.id === numberId) {
        state.dashboard.linkWhatsAppModal.selectedNumber.is_linked = false;
        state.dashboard.linkWhatsAppModal.selectedNumber.linked_by = null;
      }
    },

    resetQRState(state) {
      state.dashboard.linkWhatsAppModal.qrCode = null;
      state.dashboard.linkWhatsAppModal.isLoadingQR = false;
      state.dashboard.linkWhatsAppModal.qrError = null;
      state.dashboard.linkWhatsAppModal.isLinkedSuccessfully = false;
    },

    resetLinkWhatsAppModalFlow(state) {
      state.dashboard.linkWhatsAppModal.currentStep = 'info';
      state.dashboard.linkWhatsAppModal.isConfirmed = false;
      state.dashboard.linkWhatsAppModal.qrCode = null;
      state.dashboard.linkWhatsAppModal.isLoadingQR = false;
      state.dashboard.linkWhatsAppModal.qrError = null;
      state.dashboard.linkWhatsAppModal.isLinkedSuccessfully = false;
      state.dashboard.linkWhatsAppModal.linkedPhoneNumber = null;
      state.dashboard.linkWhatsAppModal.duplicatePhoneError = null;
    },

    setLinkWhatsAppModalDuplicateError(state, action) {
      state.dashboard.linkWhatsAppModal.duplicatePhoneError = action.payload;
      state.dashboard.linkWhatsAppModal.currentStep = 'error';
    },

    setAddNumberModalStep(state, action) {
      state.dashboard.addNumberModal.step = action.payload;
    },

    updateAddNumberForm(state, action) {
      const { field, value } = action.payload;
      state.dashboard.addNumberModal.form[field] = value;
    },

    updateFilters(state, action) {
      const { field, value } = action.payload;
      state.dashboard.filters[field] = value;

      state.dashboard.pagination.page = 1;
    },

    setPage(state, action) {
      state.dashboard.pagination.page = action.payload;
    },

    setWebSocketState(state, action) {
      state.dashboard.webSocketState = {
        ...state.dashboard.webSocketState,
        ...action.payload,
      };
    },

    setActiveConnection(state, action) {
      state.dashboard.webSocketState.activeConnection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWhatsAppSlots.pending, (state) => {
      state.dashboard.isLoading = true;
      state.dashboard.error = null;
    });
    builder.addCase(getWhatsAppSlots.fulfilled, (state, action) => {
      state.dashboard.isLoading = false;
      const apiSlots = action.payload?.slots || [];

      // Usar los datos directamente de la API
      state.dashboard.numbers = apiSlots;
      state.dashboard.availableNumbers =
        action.payload?.total || apiSlots.length;
      state.dashboard.pagination.totalItems = apiSlots.length;

      // Maximo de asignaciones
      const assignmentStatus = action.payload?.assignment_status;
      state.dashboard.assignmentStatus = assignmentStatus;
      const availabilityFlags = calculateAvailabilityFlags(assignmentStatus);
      state.dashboard.hasReachedMaxAssignments =
        availabilityFlags.hasReachedMaxAssignments;
      state.dashboard.hasReachedMaxCompany =
        availabilityFlags.hasReachedMaxCompany;
      state.dashboard.hasReachedMaxBranch =
        availabilityFlags.hasReachedMaxBranch;
      state.dashboard.hasReachedMaxAgent = availabilityFlags.hasReachedMaxAgent;
    });
    builder.addCase(getWhatsAppSlots.rejected, (state, action) => {
      state.dashboard.isLoading = false;
      state.dashboard.error = action.error.message;
    });

    builder.addCase(createWhatsAppSlot.pending, (state) => {
      state.dashboard.addNumberModal.isLoading = true;
      state.dashboard.error = null;
    });
    builder.addCase(createWhatsAppSlot.fulfilled, (state, action) => {
      state.dashboard.addNumberModal.isLoading = false;

      const slot = action.payload?.slot;

      if (slot && typeof slot === 'object' && slot.id) {
        // Agregar el slot directamente de la API
        state.dashboard.numbers.push(slot);
        state.dashboard.pagination.totalItems += 1;
        state.dashboard.availableNumbers = state.dashboard.numbers.length;

        // Guardar el slot recién creado para el modal de éxito
        state.dashboard.addNumberModal.lastAssignedSlot = slot;
      }

      // Maximo de asignaciones
      const assignmentStatus = action.payload?.assignment_status;
      state.dashboard.assignmentStatus = assignmentStatus;
      const availabilityFlags = calculateAvailabilityFlags(assignmentStatus);
      state.dashboard.hasReachedMaxAssignments =
        availabilityFlags.hasReachedMaxAssignments;
      state.dashboard.hasReachedMaxCompany =
        availabilityFlags.hasReachedMaxCompany;
      state.dashboard.hasReachedMaxBranch =
        availabilityFlags.hasReachedMaxBranch;
      state.dashboard.hasReachedMaxAgent = availabilityFlags.hasReachedMaxAgent;
    });
    builder.addCase(createWhatsAppSlot.rejected, (state, action) => {
      state.dashboard.addNumberModal.isLoading = false;
      state.dashboard.error = action.error.message;
    });

    builder.addCase(whatsAppNumberLink.pending, (state, action) => {
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === action.meta.arg.id
      );
      if (numberIndex !== -1) {
        state.dashboard.numbers[numberIndex].isLoading = true;
      }
    });
    builder.addCase(whatsAppNumberLink.fulfilled, (state, action) => {
      const { slot, isLinked } = action.payload;

      // Si es solo un cierre de modal (isLinked: true), no actualizar nada
      if (isLinked && !slot) {
        return;
      }

      // Si hay slot, actualizarlo con los datos de la API
      if (slot) {
        const numberIndex = state.dashboard.numbers.findIndex(
          (number) => number.id === slot.id
        );
        if (numberIndex !== -1) {
          // Actualizar el slot completo con los datos de la API
          state.dashboard.numbers[numberIndex] = slot;
        }
      }
    });
    builder.addCase(whatsAppNumberLink.rejected, (state, action) => {
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === action.meta.arg.id
      );
      if (numberIndex !== -1) {
        state.dashboard.numbers[numberIndex].isLoading = false;
      }
      state.dashboard.error = action.error.message;
    });

    builder.addCase(assignWhatsAppNumber.pending, (state, action) => {
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === action.meta.arg.slot_id
      );
      if (numberIndex !== -1) {
        state.dashboard.numbers[numberIndex].isLoading = true;
      }
      state.dashboard.error = null;
    });
    builder.addCase(assignWhatsAppNumber.fulfilled, (state, action) => {
      const { slot, assignment_status } = action.payload;

      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === slot.id
      );

      if (numberIndex !== -1) {
        // Actualizar el slot directamente con los datos de la API
        state.dashboard.numbers[numberIndex] = slot;
      }

      // Guardar el slot recién asignado para el modal de éxito
      state.dashboard.addNumberModal.lastAssignedSlot = slot;

      // Maximo de asignaciones
      state.dashboard.assignmentStatus = assignment_status;
      const availabilityFlags = calculateAvailabilityFlags(assignment_status);
      state.dashboard.hasReachedMaxAssignments =
        availabilityFlags.hasReachedMaxAssignments;
      state.dashboard.hasReachedMaxCompany =
        availabilityFlags.hasReachedMaxCompany;
      state.dashboard.hasReachedMaxBranch =
        availabilityFlags.hasReachedMaxBranch;
      state.dashboard.hasReachedMaxAgent = availabilityFlags.hasReachedMaxAgent;
    });
    builder.addCase(assignWhatsAppNumber.rejected, (state, action) => {
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === action.meta.arg.slot_id
      );
      if (numberIndex !== -1) {
        state.dashboard.numbers[numberIndex].isLoading = false;
      }
      state.dashboard.error = action.error.message;
    });

    builder.addCase(unassignWhatsAppNumber.pending, (state, action) => {
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === action.meta.arg
      );
      if (numberIndex !== -1) {
        state.dashboard.numbers[numberIndex].isLoading = true;
      }
      state.dashboard.error = null;
    });
    builder.addCase(unassignWhatsAppNumber.fulfilled, (state, action) => {
      const { slot, assignment_status } = action.payload;

      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === slot.id
      );

      if (numberIndex !== -1) {
        // Actualizar el slot directamente con los datos de la API
        state.dashboard.numbers[numberIndex] = slot;
      }

      // Maximo de asignaciones
      state.dashboard.assignmentStatus = assignment_status;
      const availabilityFlags = calculateAvailabilityFlags(assignment_status);
      state.dashboard.hasReachedMaxAssignments =
        availabilityFlags.hasReachedMaxAssignments;
      state.dashboard.hasReachedMaxCompany =
        availabilityFlags.hasReachedMaxCompany;
      state.dashboard.hasReachedMaxBranch =
        availabilityFlags.hasReachedMaxBranch;
      state.dashboard.hasReachedMaxAgent = availabilityFlags.hasReachedMaxAgent;
    });
    builder.addCase(unassignWhatsAppNumber.rejected, (state, action) => {
      const numberIndex = state.dashboard.numbers.findIndex(
        (number) => number.id === action.meta.arg
      );
      if (numberIndex !== -1) {
        state.dashboard.numbers[numberIndex].isLoading = false;
      }
      state.dashboard.error = action.error.message;
    });

    builder.addCase(getWhatsAppQRCode.pending, (state) => {
      state.dashboard.linkWhatsAppModal.isLoadingQR = true;
      state.dashboard.linkWhatsAppModal.qrError = null;
    });
    builder.addCase(getWhatsAppQRCode.fulfilled, (state, action) => {
      state.dashboard.linkWhatsAppModal.isLoadingQR = false;

      if (action.payload?.status === 'qr') {
        state.dashboard.linkWhatsAppModal.qrCode = action.payload.qr_code;
        state.dashboard.linkWhatsAppModal.qrError = null;
      } else if (action.payload?.status === 'ready') {
        state.dashboard.linkWhatsAppModal.qrCode = null;
        state.dashboard.linkWhatsAppModal.isLinkedSuccessfully = true;
        if (state.dashboard.linkWhatsAppModal.selectedNumber) {
          const numberIndex = state.dashboard.numbers.findIndex(
            (number) =>
              number.id === state.dashboard.linkWhatsAppModal.selectedNumber.id
          );
          if (numberIndex !== -1) {
            // Usar key de API (snake_case)
            state.dashboard.numbers[numberIndex].is_linked = true;
          }
        }
      }
    });
    builder.addCase(getWhatsAppQRCode.rejected, (state, action) => {
      state.dashboard.linkWhatsAppModal.isLoadingQR = false;
      state.dashboard.linkWhatsAppModal.qrError = action.error.message;
    });

    builder.addCase(linkCloudApiNumber.fulfilled, (state, action) => {
      const { slot, phoneNumber } = action.payload;
      state.dashboard.linkWhatsAppModal.linkedPhoneNumber = phoneNumber;
      state.dashboard.linkWhatsAppModal.isLinkedSuccessfully = true;

      if (slot) {
        const numberIndex = state.dashboard.numbers.findIndex(
          (number) => number.id === slot.id
        );
        if (numberIndex !== -1) {
          state.dashboard.numbers[numberIndex] = slot;
        }
        if (state.dashboard.linkWhatsAppModal.selectedNumber?.id === slot.id) {
          state.dashboard.linkWhatsAppModal.selectedNumber = slot;
        }
      }
    });

    builder.addCase(getCloudApiConnectionStatus.pending, (state) => {
      state.dashboard.cloudApi.isLoading = true;
      state.dashboard.cloudApi.error = null;
    });
    builder.addCase(getCloudApiConnectionStatus.fulfilled, (state, action) => {
      state.dashboard.cloudApi.isLoading = false;
      state.dashboard.cloudApi.connected = Boolean(action.payload?.connected);
      state.dashboard.cloudApi.wabaId = action.payload?.waba_id ?? null;
      state.dashboard.cloudApi.phoneNumbers =
        action.payload?.phone_numbers || [];
    });
    // `connected` solo se decide en `fulfilled`: un 5xx o un timeout no significa
    // que la WABA esté desconectada, y bajar el flag deshabilitaría Cloud API.
    builder.addCase(getCloudApiConnectionStatus.rejected, (state, action) => {
      state.dashboard.cloudApi.isLoading = false;
      state.dashboard.cloudApi.error = action.error.message;
    });

    builder.addCase(unlinkCloudApiAccount.pending, (state) => {
      state.dashboard.cloudApi.isLoading = true;
      state.dashboard.cloudApi.error = null;
    });
    builder.addCase(unlinkCloudApiAccount.rejected, (state, action) => {
      state.dashboard.cloudApi.isLoading = false;
      state.dashboard.cloudApi.error = action.error.message;
    });

    builder.addCase(getAvailableSlot.pending, (state) => {
      state.dashboard.error = null;
    });
    builder.addCase(getAvailableSlot.fulfilled, (state, action) => {
      if (action.payload?.available && action.payload?.slot) {
        state.dashboard.availableSlot = action.payload.slot;
      } else {
        state.dashboard.availableSlot = null;
      }
    });
    builder.addCase(getAvailableSlot.rejected, (state, action) => {
      state.dashboard.error = action.error.message;
      state.dashboard.availableSlot = null;
    });
  },
});

export const {
  setWhatsAppData,
  toggleAddNumberModal,
  toggleLinkWhatsAppModal,
  updateAddNumberForm,
  updateFilters,
  setPage,
  setAddNumberModalStep,
  setWhatsAppNumberLinked,
  setWhatsAppNumberUnlinked,
  resetQRState,
  setWebSocketState,
  setActiveConnection,
  setLinkWhatsAppModalStep,
  setLinkWhatsAppModalConfirmed,
  resetLinkWhatsAppModalFlow,
  setLinkWhatsAppModalDuplicateError,
  toggleLinkCloudApiAccountModal,
  toggleUnlinkCloudApiAccountModal,
  reportBillingEligibilityIssue,
  hydrateBillingEligibilityIssueBanner,
  dismissBillingEligibilityIssueBanner,
} = WhatsAppSlice.actions;

export default WhatsAppSlice.reducer;
